import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyProjects, getSettings, uploadCardBanner, getMyQuests, createQuest, deleteQuest, getOwnerSubmissions, reviewSubmission, updatePreferences } from '../api/projects';
import '../styles/Account.css';
import '../styles/ICOListing.css';

import Navbar from '../components/Navbar';
import '../styles/Boost.css';

export default function Account() {
    const navigate = useNavigate();
    const { user, authLoading, logoutUser } = useAuth();
    const [tab, setTab]           = useState('projects');
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [settings, setSettings] = useState(null);

    const [selectedOffer, setSelectedOffer] = useState(null); // { slug, pack_key, promo_type, amount }
    const [txHash, setTxHash] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitDone, setSubmitDone] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const [copied, setCopied]         = useState(false);
    const [globalSuccess, setGlobalSuccess] = useState('');

    // Upload bannière card
    const [uploadingBanner, setUploadingBanner]   = useState({});
    const [uploadBannerMsg, setUploadBannerMsg]   = useState({});

    // Quests
    const [quests, setQuests]           = useState([]);
    const [questProject, setQuestProject] = useState(null); // projet sélectionné pour gérer ses quêtes
    const [questForm, setQuestForm]     = useState({ title: '', platform: '', actions: [], url: '', reward: '', points: 10, tweetText: '' });

    // Soumissions (owner)
    const [submissionsProject, setSubmissionsProject] = useState(null);
    const [submissions, setSubmissions]               = useState([]);
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [rejectReasons, setRejectReasons]           = useState({}); // { [sub_id]: string }
    const [questLoading, setQuestLoading] = useState(false);
    const [questError, setQuestError]   = useState('');
    const [questSuccess, setQuestSuccess] = useState('');
    const [alertNewProject, setAlertNewProject] = useState(user?.alert_new_project || false);
    const [mySubmissions, setMySubmissions] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);


    const WALLET_ADDRESS = '0x9c086800A191E03CcE82d2744BaCE178870a14BB';

    const PACK_TO_SETTING = {
        'boost_single':  1,
        'boost_pack10':  2,
        'banner_week':   3,
        'banner_month':  4,
        'premium_week':  5,
        'premium_month': 6,
        'card_image':    7,
    };

    const isOwner = projects.some(p => parseInt(p.is_approved) === 1);

    useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/connexion'); return; }
    Promise.all([getMyProjects(), getSettings()]).then(([data, s]) => {
        const approved = Array.isArray(data) ? data : [];
        setProjects(approved);
        setSettings(s);
        setLoading(false);
        const hasApproved = approved.some(p => parseInt(p.is_approved) === 1);
        setTab(hasApproved ? 'projects' : 'myquests');
    });
    }, [user]);

    const colors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];

    ////////////////////////////
    // Vérifie si une offre est en attente de paiement
    ////////////////////////////
    const hasPendingPromo = (project) => {
        return project.promos && project.promos.some(p => p.status === 'pending');
    };

    const getStatusPill = (project) => {
        if (!parseInt(project.email_verified)) return <span className="status-pill pill-unverified">Email not verified</span>;
        if (!parseInt(project.is_approved)) return <span className="status-pill pill-pending">Pending</span>;
        return <span className="status-pill pill-approved">Live</span>;
    };


    const handleSubmitOffer = async () => {
        if (!txHash.trim()) { setSubmitError('Enter your tx hash.'); return; }
        setSubmitting(true); setSubmitError('');
        // Recharger les projets pour mettre à jour les promos
        Promise.all([getMyProjects(), getSettings()]).then(([data, s]) => {
            setProjects(Array.isArray(data) ? data : []);
            setSettings(s);
        });
        const token = localStorage.getItem('onlineico_token');

        // ==================== DEBUG ====================
        const payload = {
            project_id: selectedOffer.project_id,
            id_setting: PACK_TO_SETTING[selectedOffer.pack_key],
            tx_hash: txHash.trim(),
            promo_type: selectedOffer.promo_type,
        };
        // ===============================================

        let result = {};
        try {
            const response = await fetch('https://onlineico.com/api/boosts.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify(payload)
            });
            const text = await response.text();


            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('❌ Réponse non-JSON du serveur:', text);
                setSubmitError('Server error — invalid response. Please contact support.');
                setSubmitting(false);
                return;
            }
        } catch (e) {
            console.error('❌ Erreur réseau:', e);
            setSubmitError('Unable to reach the server. Please check your connection.');
            setSubmitting(false);
            return;
        }
        if (result.success) {
            setSubmitDone(true);
            setSelectedOffer(null);
            setTxHash('');
            setGlobalSuccess('✅ Your request has been recorded! It is pending validation by our team.');
            setTimeout(() => setGlobalSuccess(''), 8000);
            const updated = await getMyProjects();
            setProjects(Array.isArray(updated) ? updated : []);
        } else {
            setSubmitError(result.error || 'Submission error');
        }
        setSubmitting(false);
    };


    const handleCopy = () => {
        navigator.clipboard.writeText(WALLET_ADDRESS);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleUploadBanner = async (slug, file) => {
        if (!file) return;
        setUploadingBanner(prev => ({ ...prev, [slug]: true }));
        setUploadBannerMsg(prev => ({ ...prev, [slug]: null }));
        const result = await uploadCardBanner(slug, file);
        setUploadingBanner(prev => ({ ...prev, [slug]: false }));
        if (result.success) {
            setUploadBannerMsg(prev => ({ ...prev, [slug]: { type: 'success', text: result.message } }));
            // Rafraîchir les projets pour afficher le nouveau statut
            const data = await getMyProjects();
            setProjects(Array.isArray(data) ? data : []);
        } else {
            setUploadBannerMsg(prev => ({ ...prev, [slug]: { type: 'error', text: result.error } }));
        }
    };

    return (
        <div className="app-wrap">
            <Navbar />

            <div className="account-wrap">
                <div className="account-header">
                    <div className="account-title">My account</div>
                    <div className="account-subtitle">Manage your project and profile</div>
                </div>

                <div className="account-tabs">
                    {isOwner ? (
                        <>
                            <button className={`account-tab${tab === 'projects' ? ' active' : ''}`} onClick={() => setTab('projects')}>My project</button>
                            <button className={`account-tab${tab === 'quests' ? ' active' : ''}`} onClick={() => setTab('quests')}>🛠 Quest Manager</button>
                            <button className={`account-tab${tab === 'submissions' ? ' active' : ''}`} onClick={() => setTab('submissions')}>📋 Submissions</button>
                            <button className={`account-tab${tab === 'classement' ? ' active' : ''}`} onClick={async () => {
                                setTab('classement');
                                if (leaderboard !== null) return;
                                const approvedProject = projects.find(p => parseInt(p.is_approved) === 1);
                                if (!approvedProject) return;
                                const token = localStorage.getItem('onlineico_token');
                                const res = await fetch(`https://onlineico.com/api/auth/leaderboard.php?slug=${approvedProject.slug}`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                });
                                const data = await res.json();
                                if (data.success) setLeaderboard(data);
                            }}>🏆 Classement</button>
                        </>
                    ) : (
                        <button className={`account-tab${tab === 'myquests' ? ' active' : ''}`} onClick={async () => {
                            setTab('myquests');
                            if (mySubmissions !== null) return;
                            const token = localStorage.getItem('onlineico_token');
                            if (!token) return;
                            const res = await fetch('https://onlineico.com/api/auth/submit-quest.php', {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await res.json();
                            setMySubmissions(Array.isArray(data) ? data : []);
                        }}>⚡ My Quests</button>
                    )}
                    <button className={`account-tab${tab === 'profile' ? ' active' : ''}`} onClick={() => setTab('profile')}>My profile</button>
                </div>

                {/* NOTIFICATION GLOBALE */}
                {globalSuccess && (
                    <div style={{
                        background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)',
                        borderRadius: '14px', padding: '14px 18px', marginBottom: '20px',
                        color: '#34D399', fontWeight: '600', fontSize: '0.9rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                    }}>
                        <span>{globalSuccess}</span>
                        <button onClick={() => setGlobalSuccess('')}
                            style={{ background: 'none', border: 'none', color: '#34D399', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: 0 }}>×</button>
                    </div>
                )}

                {/* MON PROJET */}
                {tab === 'projects' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{projects.length} project{projects.length > 1 ? 's' : ''}</div>
                        </div>

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading...</div>
                        ) : projects.length === 0 ? (
                            <div className="empty-projects">
                                <div className="empty-projects-icon">🚀</div>
                                <p>You haven't submitted a project yet.</p>
                                <button className="vote-btn" style={{ marginTop: '1rem' }} onClick={() => navigate('/add-crypto-project-ico')}>Submit a project</button>
                            </div>
                        ) : (
                            projects.map(p => (
                                <div className="project-row" key={p.id}>
                                    <div className="project-row-left">
                                        <div className="project-row-logo" style={{ background: colors[p.id % colors.length] }}>
                                            {p.name.substring(0, 2).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="project-row-name">{p.name}</div>
                                            <div className="project-row-meta">{p.ticker} · {p.type.toUpperCase()} · {p.chain}</div>
                                        </div>
                                    </div>
                                    <div className="project-row-right">
                                        {getStatusPill(p)}
                                        {parseInt(p.is_approved) === 1 && (
                                            <button className="view-btn" onClick={() => navigate(`/${p.type}/${p.slug}`)}>View</button>
                                        )}
                                        {/* Bouton si offre en attente de paiement */}
                                        {parseInt(p.is_approved) === 1 && hasPendingPromo(p) && (
                                            <button className="view-btn" style={{ borderColor: 'rgba(245,158,11,0.3)', color: '#F59E0B' }}
                                                onClick={() => navigate(`/boost/${p.slug}`)}>
                                                ⚡ Activate my option
                                            </button>
                                        )}
                                        <button className="edit-btn" onClick={() => navigate(`/edit/${p.slug}`)}>Edit</button>
                                    </div>
                                </div>
                            ))
                        )}

                        {/* OFFRES PUBLICITAIRES */}

{/* OFFRES PUBLICITAIRES */}
{/* DIV BOOSTS — entre projet et offres publicitaires */}
{projects.filter(p => parseInt(p.is_approved) === 1 && parseInt(p.boost_confirmed) > 0).map(p => {
    const boostAchetes  = parseInt(p.boost_confirmed) || 0;
    const boostUtilises = parseInt(p.boosts) || 0;
    const boostRestants = boostAchetes - boostUtilises;
    return (
        <div key={`boost-stats-${p.id}`} style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '16px', padding: '16px', marginTop: '12px', marginBottom: '4px' }}>
            <div style={{ fontSize: '0.72rem', color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>⚡ My Boosts — {p.name}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1, background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ color: '#F59E0B', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.6rem', lineHeight: 1 }}>{boostAchetes}</div>
                    <div style={{ color: '#64748b', fontSize: '0.7rem', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Purchased</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ color: '#94a3b8', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.6rem', lineHeight: 1 }}>{boostUtilises}</div>
                    <div style={{ color: '#64748b', fontSize: '0.7rem', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Used</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ color: boostRestants > 0 ? '#34D399' : '#f87171', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.6rem', lineHeight: 1 }}>{boostRestants}</div>
                    <div style={{ color: '#64748b', fontSize: '0.7rem', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remaining</div>
                </div>
            </div>
        </div>
    );
})}

{settings && projects.filter(p => parseInt(p.is_approved) === 1).map(p => {
    const hasBanner  = p.promos && p.promos.some(pr => pr.promo_type === 'banner'  && (pr.status === 'confirmed' || pr.status === 'pending'));
    const hasPremium = p.promos && p.promos.some(pr => pr.promo_type === 'premium' && (pr.status === 'confirmed' || pr.status === 'pending'));
    
    const hasCardImage      = p.promos && p.promos.some(pr => pr.promo_type === 'card_image' && pr.status === 'confirmed');
    const hasPendingCardImage = p.promos && p.promos.some(pr => pr.promo_type === 'card_image' && pr.status === 'pending');

    return (
        <div key={p.id} style={{ marginTop: '32px' }}>
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', marginBottom: '24px' }} />
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>My advertising options</div>

            {/* ── 1. OFFRES DISPONIBLES À L'ACHAT ── */}

            {/* CARD IMAGE — achat si pas encore payé */}
            {!hasCardImage && !hasPendingCardImage && (
                <div style={{ background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '12px' }}>
                    <p style={{ color: '#EC4899', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 12px 0' }}>
                        🖼️ Display a banner image on your card in the ICO list. <strong style={{ color: '#F1F5F9' }}>Unlimited duration</strong> — one-time payment of <strong style={{ color: '#F1F5F9' }}>{settings.card_image} $</strong>.
                    </p>
                    <div
                        onClick={() => {
                            setSelectedOffer({ project_id: p.id, pack_key: 'card_image', promo_type: 'card_image', amount: settings.card_image });
                            setSubmitDone(false); setSubmitError(''); setTxHash('');
                        }}
                        style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(236,72,153,0.2)', borderRadius: '16px', padding: '16px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(236,72,153,0.5)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(236,72,153,0.2)'}
                    >
                        <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>🖼️</div>
                        <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Card image — unlimited</div>
                        <div style={{ color: '#EC4899', fontWeight: '700', fontSize: '1.1rem' }}>{settings.card_image} $</div>
                        <div style={{ color: '#64748b', fontSize: '0.72rem', marginTop: '4px' }}>One-time payment, no subscription</div>
                    </div>
                </div>
            )}

{/* BANNER */}
{!hasBanner && (
    <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '12px' }}>
        <p style={{ color: '#3B82F6', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 12px 0' }}>
            📢 Your banner is displayed in the right column of other projects' detail pages. Ideal for targeting investors already interested.
        </p>
        {p.promos && p.promos.some(pr => pr.promo_type === 'banner' && pr.status === 'pending') ? (
            <div style={{ color: '#F59E0B', fontSize: '0.85rem', fontWeight: '600', textAlign: 'center', padding: '8px' }}>
                ⏳ Option pending — awaiting validation
            </div>
        ) : (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[
                    { key: 'banner_week', label: '1 week', price: settings.banner_week },
                    { key: 'banner_month', label: '1 month', price: settings.banner_month }
                ].map((pack, i) => (
                    <div key={i}
                        onClick={() => { setSelectedOffer({ project_id: p.id, pack_key: pack.key, promo_type: 'banner', amount: pack.price }); setSubmitDone(false); setSubmitError(''); setTxHash(''); }}
                        style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '16px', padding: '16px', cursor: 'pointer', textAlign: 'center', flex: 1, transition: 'all 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.2)'}
                    >
                        <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>📢</div>
                        <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>{pack.label}</div>
                        <div style={{ color: '#3B82F6', fontWeight: '700', fontSize: '1.1rem' }}>{pack.price} $</div>
                    </div>
                ))}
            </div>
        )}
    </div>
)}

{/* PREMIUM */}
{!hasPremium && (
    <div style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '12px' }}>
        <p style={{ color: '#8B5CF6', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 12px 0' }}>
            ⭐ Your project is displayed as a priority in the "Featured" section on the homepage. Maximum visibility.
        </p>
        {p.promos && p.promos.some(pr => pr.promo_type === 'premium' && pr.status === 'pending') ? (
            <div style={{ color: '#F59E0B', fontSize: '0.85rem', fontWeight: '600', textAlign: 'center', padding: '8px' }}>
                ⏳ Option pending — awaiting validation
            </div>
        ) : (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[
                    { key: 'premium_week', label: '1 week', price: settings.premium_week },
                    { key: 'premium_month', label: '1 month', price: settings.premium_month },
                ].map((pack, i) => (
                    <div key={i}
                        onClick={() => { setSelectedOffer({ project_id: p.id, pack_key: pack.key, promo_type: 'premium', amount: pack.price }); setSubmitDone(false); setSubmitError(''); setTxHash(''); }}
                        style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '16px', padding: '16px', cursor: 'pointer', textAlign: 'center', flex: 1, transition: 'all 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(139,92,246,0.2)'}
                    >
                        <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>⭐</div>
                        <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>{pack.label}</div>
                        <div style={{ color: '#8B5CF6', fontWeight: '700', fontSize: '1.1rem' }}>{pack.price} $</div>
                    </div>
                ))}
            </div>
        )}
    </div>
)}


            {/* ── FORMULAIRE PAIEMENT — juste après les offres disponibles ── */}
            {selectedOffer && selectedOffer.project_id === p.id && selectedOffer.promo_type !== 'boost' && (
                <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', color: '#F1F5F9', marginBottom: '16px' }}>Payment — {selectedOffer.amount} $</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '12px' }}>
                        Send exactly <strong style={{ color: '#F59E0B' }}>{selectedOffer.amount} $</strong> in USDT on the <strong style={{ color: '#F1F5F9' }}>BSC (BEP-20)</strong> network to this address:
                    </div>
                    <div className="boost-wallet">
                        <div className="boost-wallet-title">USDT BSC Wallet Address</div>
                        <div className="boost-wallet-address" onClick={handleCopy} title="Click to copy">{WALLET_ADDRESS}</div>
                        <div style={{ textAlign: 'center', color: copied ? '#34D399' : '#94a3b8', fontSize: '0.8rem' }}>{copied ? '✓ Copied!' : 'Click to copy'}</div>
                    </div>
                    <div className="boost-amount">{selectedOffer.amount} $</div>
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label className="form-label">Transaction Hash <span style={{ color: '#EC4899' }}>*</span></label>
                        <input className="form-input" placeholder="0x..." value={txHash} onChange={e => setTxHash(e.target.value)} />
                    </div>
                    {submitError && <div style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '12px' }}>{submitError}</div>}
                    {submitDone && <div style={{ color: '#34D399', fontSize: '0.85rem', marginBottom: '12px' }}>✓ Request submitted! Pending validation.</div>}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn-submit" onClick={handleSubmitOffer} disabled={submitting} style={{ flex: 1 }}>{submitting ? 'Sending...' : '🚀 Submit'}</button>
                        <button onClick={() => setSelectedOffer(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#94a3b8', cursor: 'pointer', padding: '0 16px' }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* ── 2. OFFRES ACTIVES / BLOQUÉES ── */}

            {/* OFFRES ACTIVES */}
{/* OFFRES ACTIVES */}
{p.promos && p.promos.filter(pr => pr.status === 'confirmed').length > 0 && (
    <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Active options</div>
        {p.promos.filter(pr => pr.status === 'confirmed').map((promo, i) => {
            const desc = {
                banner:  '📢 Your banner is displayed in the right column of other projects\' detail pages.',
                premium: '⭐ Your project is displayed as a priority in the "Featured" section on the homepage.',
                boost:   '⚡ Each boost temporarily improves your ranking position for 48h.',
            }[promo.promo_type] || '';

            return (
                <div key={i} style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '12px', padding: '12px 16px', marginBottom: '8px' }}>
                    <div style={{ color: '#34D399', fontWeight: '600', fontSize: '0.85rem', marginBottom: desc ? '6px' : '0' }}>
                        ✅ {promo.label || promo.promo_type} — {promo.promo_until ? `active until ${new Date(promo.promo_until).toLocaleDateString('en-GB')}` : 'unlimited'}
                    </div>
                    {desc && <div style={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: '1.6' }}>{desc}</div>}
                </div>
            );
        })}
    </div>
)}

            {/* CARD IMAGE — upload/statut si option active */}
            {hasCardImage && (
                <div style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '12px' }}>
                    <div style={{ color: '#EC4899', fontWeight: '700', fontSize: '0.9rem', marginBottom: '12px' }}>🖼️ Card Image option activated</div>
                    {p.card_banner_status === 'approved' && (
                        <div style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ color: '#34D399', fontWeight: '600', fontSize: '0.85rem' }}>✅ Image approved — visible on the homepage</div>
                            <img src={p.card_banner_url} alt="bannière" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px' }} />
                        </div>
                    )}
                    {p.card_banner_status === 'pending' && (
                        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.85rem' }}>⏳ Image pending validation by our team</div>
                        </div>
                    )}
                    {p.card_banner_status === 'rejected' && (
                        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ color: '#f87171', fontWeight: '600', fontSize: '0.85rem' }}>❌ Image rejected — please submit a new one</div>
                        </div>
                    )}
                    {(!p.card_banner_status || p.card_banner_status === 'rejected') && (
                        <div>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '8px' }}>Submit your banner image (JPG, PNG or WebP — max 1 MB — 16:9 ratio recommended)</div>
                            <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} id={`upload-card-${p.slug}`}
                                onChange={e => { if (e.target.files[0]) handleUploadBanner(p.slug, e.target.files[0]); }} />
                            <label htmlFor={`upload-card-${p.slug}`}
                                style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '30px', background: 'rgba(236,72,153,0.12)', border: '1px solid rgba(236,72,153,0.3)', color: '#EC4899', fontWeight: '600', fontSize: '0.85rem', cursor: uploadingBanner[p.slug] ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                {uploadingBanner[p.slug] ? '⏳ Uploading...' : '📁 Choose an image'}
                            </label>
                            {uploadBannerMsg[p.slug] && (
                                <div style={{ marginTop: '8px', fontSize: '0.82rem', color: uploadBannerMsg[p.slug].type === 'success' ? '#34D399' : '#f87171' }}>{uploadBannerMsg[p.slug].text}</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* CARD IMAGE — paiement en attente admin */}
            {!hasCardImage && hasPendingCardImage && (
                <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '16px', padding: '18px', marginBottom: '12px', opacity: 0.85 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>🖼️</div>
                        <div>
                            <div style={{ color: '#F59E0B', fontWeight: '700', fontSize: '0.9rem', marginBottom: '6px' }}>⏳ Card Image request being processed</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: '1.6' }}>Your payment has been recorded and is pending validation by our team. Once confirmed, you will be able to upload your banner image from this page.</div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 3. BOOST — consommable, toujours en dernier ── */}
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '12px' }}>
                <p style={{ color: '#F59E0B', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 12px 0' }}>
                    ⚡ Give your project a push! A boost temporarily improves your ranking position for 48h. The more you boost, the higher you climb.
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {[
                        { key: 'boost_single', qty: 1,  label: '1 Boost',  price: settings.boost_single, unit: `${settings.boost_single}$ / boost` },
                        { key: 'boost_pack10', qty: 10, label: '10 Boosts', price: settings.boost_pack10, unit: `${Math.round(settings.boost_pack10 / 10)}$ / boost`, badge: `Save ${Math.round((1 - settings.boost_pack10 / (settings.boost_single * 10)) * 100)}%` },
                    ].map(pack => (
                        <div key={pack.key}
                            onClick={() => {
                                setSelectedOffer({ project_id: p.id, pack_key: pack.key, promo_type: 'boost', amount: pack.price });
                                setSubmitDone(false); setSubmitError(''); setTxHash('');
                            }}
                            style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', cursor: 'pointer', textAlign: 'center', flex: 1, transition: 'all 0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.5)'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.2)'}
                        >
                            <div style={{ color: '#F59E0B', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.5rem' }}>{pack.qty}</div>
                            <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>{pack.label}</div>
                            <div style={{ color: '#34D399', fontWeight: '700', fontSize: '1.1rem' }}>{pack.price} $</div>
                            <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{pack.unit}</div>
                            {pack.badge && <div style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399', borderRadius: '20px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600', marginTop: '6px' }}>{pack.badge}</div>}
                        </div>
                    ))}
                </div>
            </div>

{/* FORMULAIRE PAIEMENT BOOST */}
{selectedOffer && selectedOffer.project_id === p.id && selectedOffer.promo_type === 'boost' && (
    <div style={{ background: 'rgba(10,15,30,0.8)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '16px', padding: '20px', marginTop: '16px' }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', color: '#F1F5F9', marginBottom: '16px' }}>
            Payment — {selectedOffer.amount} $
        </div>
        <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '12px' }}>
            Send exactly <strong style={{ color: '#F59E0B' }}>{selectedOffer.amount} $</strong> in USDT on the <strong style={{ color: '#F1F5F9' }}>BSC (BEP-20)</strong> network to this address:
        </div>

        <div className="boost-wallet">
            <div className="boost-wallet-title">USDT BSC Wallet Address</div>
            <div className="boost-wallet-address" onClick={handleCopy} title="Click to copy">{WALLET_ADDRESS}</div>
            <div style={{ textAlign: 'center', color: copied ? '#34D399' : '#94a3b8', fontSize: '0.8rem' }}>
                {copied ? '✓ Copied!' : 'Click to copy'}
            </div>
        </div>
        <div className="boost-amount">{selectedOffer.amount} $</div>


        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label className="form-label">Transaction Hash <span style={{ color: '#EC4899' }}>*</span></label>
            <input className="form-input" placeholder="0x..." value={txHash} onChange={e => setTxHash(e.target.value)} />
        </div>
        {submitError && <div style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '12px' }}>{submitError}</div>}
        {submitDone && <div style={{ color: '#34D399', fontSize: '0.85rem', marginBottom: '12px' }}>✓ Request submitted! Pending validation.</div>}
        <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-submit" onClick={handleSubmitOffer} disabled={submitting} style={{ flex: 1 }}>
                {submitting ? 'Sending...' : '🚀 Submit'}
            </button>
            <button onClick={() => setSelectedOffer(null)}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#94a3b8', cursor: 'pointer', padding: '0 16px' }}>
                Cancel
            </button>
        </div>
    </div>
)}

        </div>
    );
})}

                    </>
                )}

                {/* MES QUÊTES — vue participant */}
                {tab === 'myquests' && (
                    <div>
                        <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '20px' }}>
                            Quests you have participated in and the status of your submissions.
                        </div>
                        {mySubmissions === null ? (
                            <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Loading...</div>
                        ) : mySubmissions.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⚡</div>
                                <div>You haven't submitted any quest yet.</div>
                                <button className="vote-btn" style={{ marginTop: '1rem' }} onClick={() => navigate('/earn')}>Browse quests</button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {mySubmissions.map(s => {
                                    const st = {
                                        pending:  { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)',  label: '⏳ Pending' },
                                        approved: { color: '#34D399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.3)',  label: '✅ Approved' },
                                        rejected: { color: '#f87171', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   label: '❌ Rejected' },
                                    }[s.status] || {};
                                    return (
                                        <div key={s.id} style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.88rem', marginBottom: '4px' }}>{s.quest_title}</div>
                                                <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                    {s.project_name} · {new Date(s.created_at).toLocaleDateString('en-GB')}
                                                </div>
                                                {s.points && s.status === 'approved' && (
                                                    <div style={{ color: '#F59E0B', fontWeight: '700', fontSize: '0.8rem', marginTop: '4px' }}>+{s.points} pts</div>
                                                )}
                                            </div>
                                            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: st.color, background: st.bg, border: `1px solid ${st.border}`, borderRadius: '20px', padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                                {st.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* QUEST MANAGER — vue owner */}
                {tab === 'quests' && (
                    <div>
                        {/* Sélection du projet */}
                        {!questProject ? (
                            <div>
                                <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '16px' }}>
                                    Select a project to manage its quests:
                                </div>
                                {projects.filter(p => parseInt(p.is_approved) === 1).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No approved project yet.</div>
                                ) : (
                                    projects.filter(p => parseInt(p.is_approved) === 1).map(p => (
                                        <div key={p.id} className="project-row" style={{ cursor: 'pointer' }}
                                            onClick={async () => {
                                                setQuestProject(p);
                                                setQuestLoading(true);
                                                const data = await getMyQuests(p.slug);
                                                setQuests(Array.isArray(data) ? data : []);
                                                setQuestLoading(false);
                                            }}>
                                            <div className="project-row-left">
                                                <div className="project-row-logo" style={{ background: colors[p.id % colors.length] }}>
                                                    {p.name.substring(0, 2).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="project-row-name">{p.name}</div>
                                                    <div className="project-row-meta">{p.ticker} · {p.type.toUpperCase()}</div>
                                                </div>
                                            </div>
                                            <div className="project-row-right">
                                                <button className="view-btn">Manage quests →</button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            <div>
                                {/* Header */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                    <button onClick={() => { setQuestProject(null); setQuests([]); setQuestError(''); setQuestSuccess(''); }}
                                        style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>
                                        ← Back
                                    </button>
                                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', color: '#F1F5F9', fontSize: '1rem' }}>
                                        Quests — {questProject.name}
                                    </div>
                                </div>

                                {/* Formulaire création */}
                                <div style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '20px', padding: '24px', marginBottom: '24px', backdropFilter: 'blur(20px)' }}>
                                    <div style={{ fontSize: '0.72rem', color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>⚡ Create a new quest</div>

                                    {(() => {
                                        const QUEST_PLATFORMS = {
                                            twitter:  { label: '𝕏 Twitter',       actions: [{ key: 'follow', label: 'Follow us' }, { key: 'retweet', label: 'Retweet' }, { key: 'like', label: 'Like' }, { key: 'tweet', label: 'Tweet a text' }] },
                                            telegram: { label: '✈ Telegram',      actions: [{ key: 'join', label: 'Join' }] },
                                            discord:  { label: '💬 Discord',      actions: [{ key: 'join', label: 'Join' }] },
                                            website:  { label: '🌐 Website',      actions: [{ key: 'visit', label: 'Visit the site' }] },
                                            custom:   { label: '✏️ Custom',       actions: [{ key: 'complete', label: 'Complete' }] },
                                        };
                                        const currentPlatform = QUEST_PLATFORMS[questForm.platform];
                                        const toggleAction = (key) => {
                                            setQuestForm(f => {
                                                const newActions = f.actions.includes(key)
                                                    ? f.actions.filter(a => a !== key)
                                                    : [...f.actions, key];
                                                // Si tweet décoché, vider tweetText et URL intent
                                                const newTweetText = newActions.includes('tweet') ? f.tweetText : '';
                                                const newUrl = newActions.includes('tweet') && f.tweetText
                                                    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(f.tweetText)}`
                                                    : (key === 'tweet' ? '' : f.url);
                                                return { ...f, actions: newActions, tweetText: newTweetText, url: newUrl };
                                            });
                                        };
                                        const hasTweetAction = questForm.actions.includes('tweet');
                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                {/* Ligne 1 : Plateforme + Récompense */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                    <div>
                                                        <label className="form-label">Platform *</label>
                                                        <select className="form-input" value={questForm.platform}
                                                            onChange={e => setQuestForm(f => ({ ...f, platform: e.target.value, actions: [] }))}>
                                                            <option value="">Select...</option>
                                                            {Object.entries(QUEST_PLATFORMS).map(([key, p]) => (
                                                                <option key={key} value={key}>{p.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="form-label">Reward *</label>
                                                        <input className="form-input" placeholder="Ex: 50 TOKENS" value={questForm.reward} onChange={e => setQuestForm(f => ({ ...f, reward: e.target.value }))} />
                                                    </div>
                                                    <div>
                                                        <label className="form-label">OnlineICO Points *</label>
                                                        <input className="form-input" type="number" min="1" placeholder="Ex: 20" value={questForm.points} onChange={e => setQuestForm(f => ({ ...f, points: parseInt(e.target.value) || 10 }))} />
                                                    </div>
                                                </div>

                                                {/* Actions dynamiques (checkboxes) */}
                                                {currentPlatform && (
                                                    <div>
                                                        <label className="form-label">Actions *</label>
                                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
                                                            {currentPlatform.actions.map(action => {
                                                                const checked = questForm.actions.includes(action.key);
                                                                // Twitter : groupes incompatibles
                                                                const isTwitter   = questForm.platform === 'twitter';
                                                                const hasFollow    = questForm.actions.includes('follow');
                                                                const hasEngagement = questForm.actions.includes('like') || questForm.actions.includes('retweet');
                                                                const hasTweet     = questForm.actions.includes('tweet');
                                                                const disabled = isTwitter && (
                                                                    (action.key === 'follow'  && (hasEngagement || hasTweet)) ||
                                                                    (action.key === 'tweet'   && (hasFollow || hasEngagement)) ||
                                                                    (['like', 'retweet'].includes(action.key) && (hasFollow || hasTweet))
                                                                );
                                                                return (
                                                                    <label key={action.key} onClick={() => !disabled && toggleAction(action.key)}
                                                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '10px', cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: disabled ? 0.35 : 1, border: checked ? '1px solid rgba(245,158,11,0.5)' : '1px solid rgba(255,255,255,0.1)', background: checked ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.03)', transition: 'all 0.15s' }}>
                                                                        <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: checked ? '2px solid #F59E0B' : '2px solid rgba(255,255,255,0.2)', background: checked ? '#F59E0B' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                                                                            {checked && <span style={{ color: '#0B1120', fontSize: '0.65rem', fontWeight: '900', lineHeight: 1 }}>✓</span>}
                                                                        </div>
                                                                        <span style={{ color: checked ? '#F59E0B' : '#94a3b8', fontSize: '0.82rem', fontWeight: checked ? '600' : '400' }}>{action.label}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Titre */}
                                                <div>
                                                    <label className="form-label">Quest title *</label>
                                                    <input className="form-input" placeholder="Ex: Follow our official Twitter account" value={questForm.title} onChange={e => setQuestForm(f => ({ ...f, title: e.target.value }))} />
                                                </div>

                                                {/* Texte du tweet (uniquement si action tweet) */}
                                                {hasTweetAction ? (
                                                    <div>
                                                        <label className="form-label">Tweet text *</label>
                                                        <textarea className="form-input" rows={3} placeholder="Ex: I just discovered @OnlineICO, the ICO/IDO listing platform! 🚀 #crypto"
                                                            value={questForm.tweetText}
                                                            onChange={e => {
                                                                const txt = e.target.value;
                                                                setQuestForm(f => ({
                                                                    ...f,
                                                                    tweetText: txt,
                                                                    url: txt ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(txt)}` : ''
                                                                }));
                                                            }}
                                                            style={{ resize: 'vertical', minHeight: '80px', lineHeight: '1.5' }}
                                                        />
                                                        {questForm.url && (
                                                            <div style={{ marginTop: '6px', fontSize: '0.72rem', color: '#64748b', wordBreak: 'break-all' }}>
                                                                🔗 Generated intent URL: <span style={{ color: '#3B82F6' }}>{questForm.url}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="form-label">URL link *</label>
                                                        <input className="form-input" placeholder="https://..." value={questForm.url} onChange={e => setQuestForm(f => ({ ...f, url: e.target.value }))} />
                                                    </div>
                                                )}

                                                {questError   && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{questError}</div>}
                                                {questSuccess  && <div style={{ color: '#34D399', fontSize: '0.85rem' }}>{questSuccess}</div>}

                                                <button className="btn-submit" style={{ alignSelf: 'flex-start', padding: '10px 24px' }}
                                                    onClick={async () => {
                                                        setQuestError(''); setQuestSuccess('');
                                                        if (!questForm.platform || questForm.actions.length === 0 || !questForm.title || !questForm.url || !questForm.reward) {
                                                            setQuestError('All fields are required and at least one action must be selected.'); return;
                                                        }
                                                        const res = await createQuest({ slug: questProject.slug, ...questForm });
                                                        if (res.success) {
                                                            setQuestSuccess('✅ Quest created successfully!');
                                                            setQuestForm({ title: '', platform: '', actions: [], url: '', reward: '', points: 10, tweetText: '' });
                                                            const data = await getMyQuests(questProject.slug);
                                                            setQuests(Array.isArray(data) ? data : []);
                                                        } else {
                                                            setQuestError(res.error || 'Error creating quest.');
                                                        }
                                                    }}>
                                                    ➕ Create quest
                                                </button>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Liste des quêtes */}
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>
                                    {quests.length} quest{quests.length !== 1 ? 's' : ''} created
                                </div>

                                {questLoading ? (
                                    <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Loading...</div>
                                ) : quests.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No quests for this project.</div>
                                ) : (
                                    quests.map(q => {
                                        const PLATFORM_LABELS = {
                                            twitter:  { label: '𝕏 Twitter',       color: '#1DA1F2' },
                                            telegram: { label: '✈ Telegram',      color: '#3B82F6' },
                                            discord:  { label: '💬 Discord',      color: '#8B5CF6' },
                                            website:  { label: '🌐 Website',     color: '#34D399' },
                                            custom:   { label: '✏️ Custom',      color: '#F59E0B' },
                                        };
                                        const ACTION_LABELS = { follow: 'Follow', retweet: 'Retweet', like: 'Like', join: 'Join', visit: 'Visit', complete: 'Complete' };
                                        const pl = PLATFORM_LABELS[q.platform] || { label: q.platform, color: '#94a3b8' };
                                        const actions = Array.isArray(q.actions) ? q.actions : [];
                                        return (
                                            <div key={q.id} style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.9rem', marginBottom: '6px' }}>{q.title}</div>
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '0.72rem', color: pl.color, fontWeight: '600', background: `${pl.color}15`, border: `1px solid ${pl.color}40`, borderRadius: '20px', padding: '2px 8px' }}>{pl.label}</span>
                                                        {actions.map(a => (
                                                            <span key={a} style={{ fontSize: '0.72rem', color: '#94a3b8', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2px 8px' }}>{ACTION_LABELS[a] || a}</span>
                                                        ))}
                                                        <span style={{ fontSize: '0.72rem', color: '#F59E0B', fontWeight: '600' }}>🎁 {q.reward}</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <a href={q.url} target="_blank" rel="noopener noreferrer"
                                                        style={{ fontSize: '0.78rem', color: '#3B82F6', textDecoration: 'none' }}>🔗 Link</a>
                                                    <button onClick={async () => {
                                                        const res = await deleteQuest(q.id);
                                                        if (res.success) {
                                                            const data = await getMyQuests(questProject.slug);
                                                            setQuests(Array.isArray(data) ? data : []);
                                                        }
                                                    }} style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#f87171', padding: '4px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* CLASSEMENT */}
                {tab === 'classement' && (
                    <div>
                        {leaderboard === null ? (
                            <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Loading...</div>
                        ) : (
                            <>
                                {/* Statistiques */}
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '16px', textAlign: 'center' }}>
                                        <div style={{ color: '#34D399', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.8rem', lineHeight: 1 }}>{leaderboard.total_participants}</div>
                                        <div style={{ color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '6px' }}>Participants</div>
                                    </div>
                                    <div style={{ flex: 1, background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '16px', textAlign: 'center' }}>
                                        <div style={{ color: '#F1F5F9', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.1rem', lineHeight: 1 }}>
                                            {(() => {
                                                const d = new Date(leaderboard.created_at);
                                                return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
                                            })()}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '6px' }}>Project created</div>
                                    </div>
                                </div>

                                {/* Classement */}
                                {leaderboard.ranking.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No participants yet.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {leaderboard.ranking.map((row, i) => {
                                            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                                            return (
                                                <div key={i} style={{ background: i < 3 ? 'rgba(245,158,11,0.05)' : 'rgba(10,15,30,0.5)', border: `1px solid ${i < 3 ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)'}`, borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                    <div style={{ width: '28px', textAlign: 'center', fontFamily: 'Syne, sans-serif', fontWeight: '800', color: '#64748b', fontSize: '0.85rem', flexShrink: 0 }}>
                                                        {medal || `#${i + 1}`}
                                                    </div>
                                                    <div style={{ flex: 1, color: '#F1F5F9', fontWeight: '600', fontSize: '0.88rem' }}>{row.username}</div>
                                                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{row.quests_done} quest{row.quests_done > 1 ? 's' : ''}</div>
                                                    <div style={{ color: '#F59E0B', fontWeight: '700', fontFamily: 'Syne, sans-serif', fontSize: '0.95rem', minWidth: '60px', textAlign: 'right' }}>+{row.total_points} pts</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* SOUMISSIONS */}
                {tab === 'submissions' && (
                    <div>
                        {!submissionsProject ? (
                            // Sélection du projet
                            <div>
                                <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '20px' }}>
                                    Select a project to view its quest submissions.
                                </div>
                                {projects.filter(p => p.is_approved).length === 0 ? (
                                    <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No approved project.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {projects.filter(p => p.is_approved).map(p => (
                                            <div key={p.id}
                                                onClick={async () => {
                                                    setSubmissionsProject(p);
                                                    setSubmissionsLoading(true);
                                                    const data = await getOwnerSubmissions(p.slug);
                                                    setSubmissions(Array.isArray(data) ? data : []);
                                                    setSubmissionsLoading(false);
                                                }}
                                                style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{p.name}</span>
                                                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>View submissions →</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                {/* Header */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                    <button onClick={() => { setSubmissionsProject(null); setSubmissions([]); }}
                                        style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>
                                        ← Back
                                    </button>
                                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', color: '#F1F5F9', fontSize: '1rem' }}>
                                        Submissions — {submissionsProject.name}
                                    </div>
                                </div>

                                {submissionsLoading ? (
                                    <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Loading...</div>
                                ) : submissions.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No submissions for this project.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {submissions.map(s => {
                                            const statusStyle = {
                                                pending:  { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)',  label: '⏳ Pending' },
                                                approved: { color: '#34D399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.3)',  label: '✅ Approved' },
                                                rejected: { color: '#f87171', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   label: '❌ Rejected' },
                                            }[s.status] || {};
                                            return (
                                                <div key={s.id} style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px 18px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                                        <div>
                                                            <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.9rem' }}>{s.quest_title}</div>
                                                            <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '2px' }}>
                                                                by <span style={{ color: '#94a3b8' }}>{s.username}</span> · {s.user_email} · {new Date(s.created_at).toLocaleDateString('en-GB')}
                                                            </div>
                                                        </div>
                                                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: statusStyle.color, background: statusStyle.bg, border: `1px solid ${statusStyle.border}`, borderRadius: '20px', padding: '3px 10px' }}>
                                                            {statusStyle.label}
                                                        </span>
                                                    </div>

                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                                        <a href={s.proof_url} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: '#3B82F6', fontSize: '0.82rem', textDecoration: 'none', wordBreak: 'break-all' }}>
                                                            🔗 {s.proof_url}
                                                        </a>
                                                        {s.status === 'pending' && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, minWidth: '220px' }}>
                                                                <input
                                                                    value={rejectReasons[s.id] || ''}
                                                                    onChange={e => setRejectReasons(prev => ({ ...prev, [s.id]: e.target.value }))}
                                                                    placeholder="Rejection reason (optional)"
                                                                    style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F1F5F9', fontSize: '0.78rem', fontFamily: 'Inter, sans-serif', outline: 'none' }}
                                                                />
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <button onClick={async () => {
                                                                        await reviewSubmission(s.id, 'approve', '');
                                                                        const data = await getOwnerSubmissions(submissionsProject.slug);
                                                                        setSubmissions(Array.isArray(data) ? data : []);
                                                                    }} style={{ flex: 1, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '8px', color: '#34D399', padding: '5px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>
                                                                        ✅ Approuver (+{s.points} pts)
                                                                    </button>
                                                                    <button onClick={async () => {
                                                                        await reviewSubmission(s.id, 'reject', rejectReasons[s.id] || '');
                                                                        const data = await getOwnerSubmissions(submissionsProject.slug);
                                                                        setSubmissions(Array.isArray(data) ? data : []);
                                                                    }} style={{ flex: 1, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#f87171', padding: '5px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>
                                                                        ❌ Rejeter
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* MON PROFIL */}
                {tab === 'profile' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                        {/* INFOS COMPTE */}
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}  >
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #34D399, #3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.2rem', color: '#0B1120', flexShrink: 0 }}>
                                {user?.username?.substring(0, 2).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ color: '#F1F5F9', fontWeight: '700', fontSize: '1rem', marginBottom: '4px' }}>{user?.username}</div>
                                <div style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: '4px' }}>{user?.email}</div>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', background: user?.role === 'admin' ? 'rgba(139,92,246,0.1)' : 'rgba(52,211,153,0.1)', color: user?.role === 'admin' ? '#a78bfa' : '#34D399', border: user?.role === 'admin' ? '1px solid rgba(139,92,246,0.3)' : '1px solid rgba(52,211,153,0.3)' }}>
                                    {user?.role === 'admin' ? '⚙ Admin' : '👤 Member'}
                                </div>
                            </div>
                        </div>

                        {/* ALERTES EMAIL */}
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                            <div>
                                <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.88rem', marginBottom: '4px' }}>🔔 New project alerts</div>
                                <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Receive an email when a new project is listed on OnlineICO.</div>
                            </div>
                            <div
                                onClick={async () => {
                                    const newVal = !alertNewProject;
                                    setAlertNewProject(newVal);
                                    await updatePreferences({ alert_new_project: newVal });
                                    const stored = JSON.parse(localStorage.getItem('onlineico_user') || '{}');
                                    localStorage.setItem('onlineico_user', JSON.stringify({ ...stored, alert_new_project: newVal }));
                                }}
                                style={{ width: '44px', height: '24px', borderRadius: '12px', background: alertNewProject ? '#34D399' : 'rgba(255,255,255,0.1)', border: alertNewProject ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative', transition: 'all 0.2s', flexShrink: 0 }}>
                                <div style={{ position: 'absolute', top: '3px', left: alertNewProject ? '22px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}
